import { useContext } from 'react';
import { suspend } from 'suspend-react';
import * as THREE from 'three';
import { getPositionsAndIndices } from '@recast-navigation/three';

import { init, RecastConfig } from '@recast-navigation/core';
import { NavMeshQuery } from '@recast-navigation/core';

import { useNavMeshGenerationWorker } from './useNavMeshGenerator';
import { mergeObjectGeometries } from '../three/mergeObjectGeometries';
import { NavMeshGeneratorInternalContext } from './NavMeshGeneratorContext';

const deferred = init();

const NavMeshGenerationSymbol = Symbol.for('NavMeshGeneration');

export interface NavMeshHookOptions {
  config?: Partial<RecastConfig>;
  keepIntermediates?: boolean;
}

/**
 * Hook to generate a navigation mesh for a 3D object.
 * @param object The 3D object to generate the nav mesh for.
 * @param param1 Options for the nav mesh generation.
 */
export const useNavMesh = (object: THREE.Object3D, opts?: NavMeshHookOptions) => {
  const { config, keepIntermediates } = { ...opts };

  const ctx = useContext(NavMeshGeneratorInternalContext);
  if (!ctx) {
    throw new Error('NavMesh must be used within a NavMeshProvider');
  }
  const { setContext, cache } = ctx;

  const generateNavMesh = useNavMeshGenerationWorker();

  if (cache.has(object.uuid)) {
    const cachedResult = cache.get(object.uuid);
    if (cachedResult) {
      console.debug(
        'Using cached nav mesh for',
        object.uuid,
        cachedResult.navMesh ? 'success' : 'failure',
      );
      setContext({
        navMesh: cachedResult.navMesh,
        query: cachedResult.navMesh ? new NavMeshQuery(cachedResult.navMesh) : null,
        success: true,
        intermediates: cachedResult.intermediates,
      });
      return;
    }
  }

  suspend(async () => {
    await deferred;

    const jobId = object.uuid;

    const geometry = mergeObjectGeometries(object, ({ name }) => {
      return /(semi|fairway|green|bunker|tee)/i.test(name);
    });

    const [positions, indices] = getPositionsAndIndices([new THREE.Mesh(geometry)]);

    const result = await generateNavMesh({
      positions,
      indices,
      config,
      jobId,
      keepIntermediates,
    });

    cache.set(jobId, result);

    const { navMesh, intermediates } = result;

    setContext({
      navMesh: navMesh,
      query: navMesh ? new NavMeshQuery(navMesh) : null,
      success: true,
      intermediates,
    });
  }, [NavMeshGenerationSymbol, object.uuid, config, keepIntermediates]);
};
